import React from "react";

import { Modal } from "../../../common/Modal";
import { CompanyMark } from "../../../common/u1/CompanyMark";
import { cn } from "../../../common/u1/cn";
import {
  BriefcaseIcon,
  ClockIcon,
  CloseIcon,
  ShieldIcon,
} from "../../../../assets/ui/icons";

/* The popup behind "View" on the header's Payment endorsements block. */
const ROLE_LABELS = {
  supplier: "Supplier",
  service_provider: "Service provider",
};

const DURATION_LABELS = {
  "<1": "Less than 1 year",
  "1-3": "1–3 years",
  "3-5": "3–5 years",
  "5-10": "5–10 years",
  "10+": "10+ years",
};

/* `last_business` comes back as a date ("2025-01-01") or, on older rows, the
   raw "YYYY-MM" the form sent. Either reads as "Jan 2025". */
export const formatLastBusiness = (value) => {
  if (!value) return "—";
  const [year, month] = String(value).split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (!year || !month || Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

/* Endorser names arrive however they were typed at signup — "neo", "pushpak fifteen · dev" — and a card of lowercase names reads as unfinished. */
export const toDisplayCase = (value) =>
  String(value ?? "").replace(/[A-Za-z\u00C0-\u024F]+/g, (word) =>
    /[A-Z]/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)
  );

/* Label above value reads as a form field; the design wants a fact, so the
   glyph carries the meaning and the label sits beside the value. */
const Detail = ({ icon: Icon, label, children }) => (
  <div className="flex min-w-0 items-start gap-2">
    <span
      aria-hidden="true"
      className="mt-[3px] flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md bg-white text-[var(--muted)] ring-1 ring-[var(--line)]"
    >
      <Icon size={12} />
    </span>
    <span className="min-w-0">
      <span className="block text-[11px] leading-[1.35] text-[var(--muted)]">
        {label}
      </span>
      <span className="block text-[13px] font-semibold leading-[1.35] text-[var(--ink)]">
        {children}
      </span>
    </span>
  </div>
);

const RolePill = ({ role }) => (
  <span className="shrink-0 rounded-full bg-[var(--coral-soft)] px-2.5 py-[3px] text-[11px] font-bold uppercase tracking-[.04em] text-[var(--coral-deep)]">
    {ROLE_LABELS[role] ?? role}
  </span>
);

export const EndorsementListModal = ({ endorsements = [], onClose }) => (
  <Modal modalHandle={onClose} noClose bare>
    <div
      className={cn(
        "u1-root flex max-h-[85vh] w-[min(92vw,560px)] flex-col overflow-hidden",
        "bg-white text-left font-sans",
        "rounded-[var(--radius-modal)] shadow-[shadow:var(--shadow-lg)]"
      )}
    >
      <header className="flex shrink-0 items-start gap-3 border-b border-[var(--line)] bg-[var(--slate-2)] px-5 py-4 sm:px-6">
        <span
          aria-hidden="true"
          className="mt-[2px] flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--coral-soft)] text-[var(--coral)]"
        >
          <ShieldIcon size={17} />
        </span>

        <div className="min-w-0 grow">
          <h2 className="m-0 text-[17px] font-bold leading-tight text-[var(--ink)]">
            Payment endorsements
          </h2>
          <p className="mt-1 mb-0 text-[12.5px] leading-[1.45] text-[var(--muted)]">
            {endorsements.length === 1
              ? "1 counterparty vouches"
              : `${endorsements.length} counterparties vouch`}{" "}
            that this business pays on time.
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
            "bg-white text-[var(--muted)] ring-1 ring-[var(--line)] transition-colors",
            "hover:bg-[var(--line)] hover:text-[var(--ink)] focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-[var(--coral)]"
          )}
        >
          <CloseIcon size={13} />
        </button>
      </header>

      <ul className="m-0 min-h-0 grow list-none space-y-2.5 overflow-y-auto p-4 sm:p-5">
        {endorsements.map((item) => (
          <li
            key={item.endorsementID}
            className="rounded-[14px] border border-[var(--line)] bg-white p-3.5 transition-colors hover:border-[var(--line-2)] sm:p-4"
          >
            <div className="flex items-start gap-3">
              <CompanyMark
                company={{
                  name: item.endorser_company || item.endorser_name || "",
                  companyID: item.endorsementID,
                }}
                size="sm"
              />

              <div className="min-w-0 grow">
                <div className="flex items-start justify-between gap-3">
                  <p className="m-0 truncate text-[14.5px] font-bold leading-tight text-[var(--ink)]">
                    {toDisplayCase(item.endorser_company || item.endorser_name) || "—"}
                  </p>
                  <RolePill role={item.business_role} />
                </div>
                <p className="mt-1 mb-0 truncate text-[12.5px] leading-tight text-[var(--muted)]">
                  {[item.endorser_name, item.endorser_designation]
                    .filter(Boolean)
                    .map(toDisplayCase)
                    .join(" · ")}
                </p>
              </div>
            </div>

            {/* The two facts that qualify the vouch — how long, how recently.
                They stack under 400px rather than squeezing to two words a
                line. */}
            <dl className="mt-3 grid grid-cols-1 gap-2.5 rounded-[10px] bg-[var(--slate-2)] p-3 min-[400px]:grid-cols-2">
              <Detail icon={ClockIcon} label="Worked together">
                {DURATION_LABELS[item.duration_of_business] ??
                  item.duration_of_business}
              </Detail>
              <Detail icon={BriefcaseIcon} label="Last invoice">
                {formatLastBusiness(item.last_business)}
              </Detail>
            </dl>
          </li>
        ))}
      </ul>
    </div>
  </Modal>
);

export default EndorsementListModal;
